import { v4 as uuidv4 } from "uuid";
import { isAxiosError } from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { cloneDeep, isEqual } from "lodash";
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    FieldErrors,
    FieldValues,
    UseFormGetValues,
    UseFormHandleSubmit,
    UseFormTrigger,
} from "react-hook-form";
import {
    EditNoteFormsPayload,
    FamilyInitialDiagnosticEvaluationNotePayload,
    FamilyIntakeNotePayload,
    ClientLocations,
    FamilyTreatmentReviewNote,
    NoteTypes,
    ProviderLocations,
} from "../types/notes.types";
import {
    AppointmentLocation,
    AppointmentLocationTeletherapy,
    SessionPlaceOfServiceCode,
} from "../../../utils/types/session";
import {
    formatZonedTimeToUtc,
    handleFormatDatePickerValue,
    showUserLocalTime,
} from "../../../utils/helpers";
import {
    useEditDraftNote,
    useSignSessionNote,
} from "../../../hooks/mutations/note";
import { useFetchSessionNote } from "../../../hooks/queries/note";
import useToast from "../../../hooks/useToast";
import { NoteMissingField, useNoteEditContext } from "../context/notes.context";
import { useAppSelector } from "../../../hooks/useRedux";
import {
    useAddNoteContentDocument,
    useDeleteNoteContentDocument,
} from "./notes.mutations";

type RemovedCommunicationFactorFields = {
    communication_factors_impacting_treatment?: unknown;
    details_around_communication_factors_impacting_treatment?: unknown;
};

const NOTE_TYPES_WITH_COMMUNICATION_FACTOR_FIELDS: string[] = [
    NoteTypes.EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED,
    NoteTypes.EVALUATION_MANAGEMENT_NOTE_MED,
    NoteTypes.TREATMENT_REVIEW_NOTE_MED,
    NoteTypes.INTAKE_NOTE_MED,
];

const stripRemovedCommunicationFactorFields = (
    payload: EditNoteFormsPayload,
    noteType?: string
): EditNoteFormsPayload => {
    if (
        noteType &&
        NOTE_TYPES_WITH_COMMUNICATION_FACTOR_FIELDS.includes(noteType)
    ) {
        return payload;
    }

    const sanitizedPayload: EditNoteFormsPayload &
        RemovedCommunicationFactorFields = { ...payload };

    delete sanitizedPayload.communication_factors_impacting_treatment;
    delete sanitizedPayload.details_around_communication_factors_impacting_treatment;

    return sanitizedPayload;
};

const NOTE_AUTOSAVE_DELAY = 2000;
const NOTE_AUTOSAVE_RETRY_DELAY = 5000;
const NOTE_AUTOSAVE_MAX_RETRIES = 3;

const findFirstErrorMessage = (errors: unknown): string | undefined => {
    if (!errors || typeof errors !== "object") return undefined;

    const { message } = errors as { message?: unknown };

    if (typeof message === "string" && message) return message;

    return Object.entries(errors)
        .filter(([key]) => key !== "ref")
        .map(([, value]) => findFirstErrorMessage(value))
        .find(Boolean);
};

const collectErrorPaths = (errors: unknown, path = ""): string[] => {
    if (!errors || typeof errors !== "object") return [];

    const { message, type } = errors as { message?: unknown; type?: unknown };

    if (message || type) return [path];

    return Object.entries(errors)
        .filter(([key]) => key !== "ref")
        .flatMap(([key, value]) =>
            collectErrorPaths(value, path ? `${path}.${key}` : key)
        );
};

const NOTE_MISSING_FIELD_SELECTOR =
    '#edit-note-form [data-invalid="true"], #edit-note-form .note-error';

const NOTE_FIELD_CONTROL_SELECTOR =
    '[aria-invalid="true"], input:not([type="hidden"]), textarea, button';

const NOTE_GROUP_LABEL_SELECTOR =
    ".note-label, .note-label-req, .note-label-opt";

const getGroupLabel = (error: HTMLElement) => {
    const group = error.previousElementSibling;
    const label = [group, group?.previousElementSibling].find((element) =>
        element?.matches(NOTE_GROUP_LABEL_SELECTOR)
    );

    return label?.textContent?.trim();
};

const getMissingFieldLabel = (field: HTMLElement) => {
    if (field.matches(".note-error")) {
        return (
            getGroupLabel(field) ||
            field.textContent?.trim() ||
            "Required field"
        );
    }

    const labelText = Array.from(field.querySelector("label")?.childNodes ?? [])
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent)
        .join("")
        .trim();

    return (
        labelText ||
        field.querySelector('[id$="-error"]')?.textContent?.trim() ||
        field.textContent?.trim() ||
        "Required field"
    );
};

const findMissingFields = (): NoteMissingField[] =>
    Array.from(
        document.querySelectorAll<HTMLElement>(NOTE_MISSING_FIELD_SELECTOR)
    ).map((element) => ({ element, label: getMissingFieldLabel(element) }));

export const goToMissingField = (field: HTMLElement) => {
    const container = field.matches(".note-error")
        ? (field.previousElementSibling as HTMLElement | null) ?? field
        : field;

    container.scrollIntoView?.({ behavior: "smooth", block: "center" });
    container
        .querySelector<HTMLElement>(NOTE_FIELD_CONTROL_SELECTOR)
        ?.focus({ preventScroll: true });
};

interface UseNoteValidationFeedbackOptions<T extends FieldValues> {
    errors: FieldErrors<T>;
    submitCount: number;
    /** The form's signing flag, so signing rules can be re-applied */
    isSigningRef: MutableRefObject<boolean>;
    trigger: UseFormTrigger<T>;
}

/**
 * Lists the fields that block signing after a failed submit and keeps
 * that list (and the inline errors) visible while autosave runs
 */
export function useNoteValidationFeedback<T extends FieldValues>({
    errors,
    submitCount,
    isSigningRef,
    trigger,
}: UseNoteValidationFeedbackOptions<T>) {
    const { setMissingFields, signAttemptRef, restoreSigningValidationRef } =
        useNoteEditContext();
    const isReviewingRef = useRef(false);
    const handledSubmitCountRef = useRef(submitCount);
    const errorKey = collectErrorPaths(errors).join("|");

    const restoreSigningValidation = useCallback(async () => {
        if (!isReviewingRef.current) return;

        // eslint-disable-next-line no-param-reassign
        isSigningRef.current = true;

        if (await trigger()) setMissingFields([]);
    }, [isSigningRef, setMissingFields, trigger]);

    useEffect(() => {
        if (!restoreSigningValidationRef) return undefined;

        restoreSigningValidationRef.current = restoreSigningValidation;

        return () => {
            restoreSigningValidationRef.current = null;
        };
    }, [restoreSigningValidation, restoreSigningValidationRef]);

    useEffect(() => {
        let shouldGoToFirstField = false;

        if (handledSubmitCountRef.current !== submitCount) {
            handledSubmitCountRef.current = submitCount;

            if (signAttemptRef?.current) {
                signAttemptRef.current = false;
                isReviewingRef.current = Boolean(errorKey);
                shouldGoToFirstField = Boolean(errorKey);
            } else if (isReviewingRef.current && !isSigningRef.current) {
                restoreSigningValidation();
                return;
            }
        }

        if (!isReviewingRef.current) {
            setMissingFields([]);
            return;
        }

        const missingFields = findMissingFields();

        setMissingFields(missingFields);

        if (shouldGoToFirstField && missingFields[0]) {
            goToMissingField(missingFields[0].element);
        }
    }, [
        errorKey,
        isSigningRef,
        restoreSigningValidation,
        setMissingFields,
        signAttemptRef,
        submitCount,
    ]);

    useEffect(() => () => setMissingFields([]), [setMissingFields]);
}

interface UseNoteAutosaveOptions<T extends FieldValues> {
    /** Current form values from `watch()` */
    watchedValues: T;
    getValues: UseFormGetValues<T>;
    handleSubmit: UseFormHandleSubmit<T>;
    /** Reset before each autosave so only draft validation rules apply */
    isSigningRef: MutableRefObject<boolean>;
    /** Resolves to true only when the note was saved */
    onSave: (values: T) => Promise<boolean>;
    onChange?: () => void;
}

export function useNoteAutosave<T extends FieldValues>(
    options: UseNoteAutosaveOptions<T>
) {
    const { watchedValues } = options;
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const seenValuesRef = useRef<T | null>(null);
    const changeVersionRef = useRef(0);
    const hasPendingChangesRef = useRef(false);
    const isSavingRef = useRef(false);
    const shouldSaveAgainRef = useRef(false);
    const retryCountRef = useRef(0);
    const isMountedRef = useRef(true);
    const { setAutosaveStatus, restoreSigningValidationRef } =
        useNoteEditContext();
    const saveRef = useRef<() => Promise<void>>(() => Promise.resolve());
    const optionsRef = useRef(options);

    optionsRef.current = options;

    if (seenValuesRef.current === null) {
        seenValuesRef.current = cloneDeep(watchedValues);
    }

    const cancelScheduledSave = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const scheduleSave = useCallback(
        (delay: number) => {
            cancelScheduledSave();
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                saveRef.current();
            }, delay);
        },
        [cancelScheduledSave]
    );

    const skipCurrentValues = useCallback(() => {
        cancelScheduledSave();
        seenValuesRef.current = cloneDeep(optionsRef.current.getValues());
        hasPendingChangesRef.current = false;
    }, [cancelScheduledSave]);

    saveRef.current = async () => {
        cancelScheduledSave();

        if (isSavingRef.current) {
            shouldSaveAgainRef.current = true;
            return;
        }

        const { handleSubmit, isSigningRef, onSave } = optionsRef.current;
        const savedChangeVersion = changeVersionRef.current;
        let didSave = false;
        let validationError: string | null = null;

        isSavingRef.current = true;
        isSigningRef.current = false;

        try {
            await handleSubmit(
                async (values) => {
                    await restoreSigningValidationRef?.current?.();
                    didSave = await onSave(values);
                },
                async (errors) => {
                    await restoreSigningValidationRef?.current?.();
                    validationError =
                        findFirstErrorMessage(errors) ??
                        "fix the highlighted fields";
                }
            )();
        } catch {
            didSave = false;
        } finally {
            isSavingRef.current = false;
        }

        const shouldSaveAgain = shouldSaveAgainRef.current;
        shouldSaveAgainRef.current = false;

        if (validationError) {
            setAutosaveStatus({ error: `Not autosaved: ${validationError}` });
            return;
        }

        if (didSave) {
            if (changeVersionRef.current === savedChangeVersion) {
                hasPendingChangesRef.current = false;
            } else if (shouldSaveAgain) {
                saveRef.current();
            } else if (isMountedRef.current && !timerRef.current) {
                scheduleSave(NOTE_AUTOSAVE_DELAY);
            }
            return;
        }

        if (
            isMountedRef.current &&
            !timerRef.current &&
            hasPendingChangesRef.current &&
            retryCountRef.current < NOTE_AUTOSAVE_MAX_RETRIES
        ) {
            retryCountRef.current += 1;
            scheduleSave(
                NOTE_AUTOSAVE_RETRY_DELAY * 2 ** (retryCountRef.current - 1)
            );
        }
    };

    useEffect(() => {
        if (isEqual(seenValuesRef.current, watchedValues)) return;

        seenValuesRef.current = cloneDeep(watchedValues);
        changeVersionRef.current += 1;
        hasPendingChangesRef.current = true;
        retryCountRef.current = 0;
        optionsRef.current.onChange?.();
        scheduleSave(NOTE_AUTOSAVE_DELAY);
    }, [watchedValues, scheduleSave]);

    useEffect(() => {
        isMountedRef.current = true;

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!hasPendingChangesRef.current) return;

            event.preventDefault();
            // eslint-disable-next-line no-param-reassign
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            isMountedRef.current = false;
            window.removeEventListener("beforeunload", handleBeforeUnload);
            cancelScheduledSave();

            if (hasPendingChangesRef.current) saveRef.current();
        };
    }, [cancelScheduledSave]);

    return { cancelScheduledSave, skipCurrentValues };
}

export function useEditNote() {
    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const [uploadProgress, setUploadProgress] = useState(0);
    const noteScopeKey = `${clientId}:${noteId}`;
    const localVersionRef = useRef<string | null | undefined>(undefined);
    const hasEditConflictRef = useRef(false);
    const isNoteSignedRef = useRef(false);
    const hasLeftNoteRef = useRef(false);
    const { setAutosaveStatus, setHasEditConflict, versionRef, runExclusive } =
        useNoteEditContext();
    const noteVersionRef = versionRef ?? localVersionRef;
    const noteScopeKeyRef = useRef(noteScopeKey);

    if (noteScopeKeyRef.current !== noteScopeKey) {
        noteScopeKeyRef.current = noteScopeKey;
        localVersionRef.current = undefined;
        hasEditConflictRef.current = false;
        isNoteSignedRef.current = false;
        hasLeftNoteRef.current = false;
    }

    const addNoteContentDoc = useAddNoteContentDocument();
    const deleteNoteContentDoc = useDeleteNoteContentDocument();

    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { toast } = useToast();

    const { practice } = useAppSelector((state) => state.userPractice);

    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    if (noteVersionRef.current === undefined && data) {
        noteVersionRef.current = data.data.note_last_modified_at ?? null;
    }

    const editDraftNote = useEditDraftNote();
    const signSessionNote = useSignSessionNote();

    const validateInputs = (rawPayload: EditNoteFormsPayload) => {
        const payload = stripRemovedCommunicationFactorFields(
            rawPayload,
            data?.data.type
        );

        const getConditionalDescribeValue = <T extends object>(
            source: T,
            answerField: string,
            describeField: string
        ): string | undefined => {
            if (!(answerField in source) || !(describeField in source)) {
                return undefined;
            }

            const answer = source[answerField as keyof T];
            const describe = source[describeField as keyof T];

            if (typeof answer === "string" && answer.toUpperCase() === "NO") {
                return undefined;
            }

            return typeof describe === "string" ? describe : undefined;
        };

        let diagnosisCodes: string[] | null | undefined = null;
        // we have this field in safety plan note
        let whenShouldFollowUpBeDoneForThisClient: string | null = null;
        // we have these fields in termination note
        let lastSessionDate: string | null = null;
        let firstSessionDate: string | null = null;
        let dateOfNextSession: string | null = null;
        let dateOfFirstSession: string | null = null;
        let dateOfLastSession: string | null = null;
        let recordOfDisclosureOfPhiEphiDate: string | null = null;
        let evaluationDate: string | null = null;
        let dateOfUpdate: string | null = null;
        let dateCreated: string | null = null;
        let dateOfLastExam: string | null = null;
        let dateOfReview: string | null = null;
        let expectedTerminationDate: string | null = null;
        let detailsForDiminishedPoorLimitedQualityPatientOrientedEvidence = "";
        let arrayOfdetailsForDiminishedPoorLimitedQualityPatientOrientedEvidences: string[] =
            [];
        let family: (
            | FamilyInitialDiagnosticEvaluationNotePayload
            | FamilyIntakeNotePayload
            | FamilyTreatmentReviewNote
        )[] = [];
        let deliveryOption: string | undefined;
        let otherDeliveryOption: string | undefined;
        let otherFrequencyOfSession: string | undefined;
        let otherLocationOfTheClient: string | undefined;
        let otherLocationOfTheProvider: string | undefined;
        let providerLocation: string | undefined;
        let otherProviderLocation: string | undefined;
        let consentWasReceivedForTheTeletherapySession: boolean | undefined;
        let theTeletherapySessionWasConductedViaVideo: boolean | undefined;
        let posCode: string | undefined;
        let sessionStartTime: string | undefined;
        let sessionEndTime: string | undefined;
        let secondaryDiagnosisCode: string | null = null;
        let tertiaryDiagnosisCode: string | null = null;
        let additionalDiagnosisOne: string | null = null;
        let additionalDiagnosisTwo: string | null = null;
        let anxietyDetails: string | undefined;
        let anxietyFrequency: string | undefined;
        let panicAttacksDetails: string | undefined;
        let panicAttacksFrequency: string | undefined;
        let depressionDetails: string | undefined;
        let depressionFrequency: string | undefined;

        if ("diagnosis_codes" in payload) {
            /* this is coming in as an array of string(codes) */
            diagnosisCodes = payload.diagnosis_codes;
        }

        if ("secondary_diagnosis" in payload) {
            secondaryDiagnosisCode =
                payload.secondary_diagnosis &&
                payload.secondary_diagnosis?.length > 0
                    ? payload.secondary_diagnosis?.[0]
                    : null;
        }

        if ("tertiary_diagnosis" in payload) {
            tertiaryDiagnosisCode =
                payload.tertiary_diagnosis &&
                payload.tertiary_diagnosis.length > 0
                    ? payload.tertiary_diagnosis?.[0]
                    : null;
        }

        if ("additional_diagnosis_one" in payload) {
            additionalDiagnosisOne =
                payload.additional_diagnosis_one &&
                payload.additional_diagnosis_one.length > 0
                    ? payload.additional_diagnosis_one?.[0]
                    : null;
        }

        if ("additional_diagnosis_two" in payload) {
            additionalDiagnosisTwo =
                payload.additional_diagnosis_two &&
                payload.additional_diagnosis_two.length > 0
                    ? payload.additional_diagnosis_two?.[0]
                    : null;
        }

        if ("consent_was_received_for_the_teletherapy_session" in payload) {
            consentWasReceivedForTheTeletherapySession =
                payload.consent_was_received_for_the_teletherapy_session;
        }

        if ("appointment_location" in payload) {
            if (
                payload.appointment_location === AppointmentLocation.IN_PERSON
            ) {
                deliveryOption = undefined;
                posCode = SessionPlaceOfServiceCode.OFFICE;
                otherDeliveryOption = undefined;
                consentWasReceivedForTheTeletherapySession = undefined;
                theTeletherapySessionWasConductedViaVideo = undefined;
                providerLocation = undefined;
                otherProviderLocation = undefined;
            } else if (
                payload.appointment_location === AppointmentLocation.TELETHERAPY
            ) {
                deliveryOption = payload.delivery_option;
                otherDeliveryOption = payload.other_delivery_option;
                consentWasReceivedForTheTeletherapySession =
                    payload.consent_was_received_for_the_teletherapy_session;
                theTeletherapySessionWasConductedViaVideo =
                    payload.the_teletherapy_session_was_conducted_via_video;
                providerLocation = payload.provider_location;
                otherProviderLocation = payload.other_provider_location;

                if (
                    payload.delivery_option ===
                        AppointmentLocationTeletherapy.HOME ||
                    payload.delivery_option ===
                        AppointmentLocationTeletherapy.OFFICE ||
                    payload.delivery_option ===
                        AppointmentLocationTeletherapy.OTHER
                ) {
                    posCode =
                        SessionPlaceOfServiceCode.TELEHEALTH_PROVIDED_IN_PATIENTS_HOME;
                }

                if (
                    payload.delivery_option !==
                    AppointmentLocationTeletherapy.OTHER
                ) {
                    otherDeliveryOption = undefined;
                }

                if (
                    payload.provider_location !==
                    AppointmentLocationTeletherapy.OTHER
                ) {
                    otherProviderLocation = undefined;
                }
            }
        }

        if ("frequency_of_sessions" in payload) {
            const hasOtherFrequency = Array.isArray(
                payload.frequency_of_sessions
            )
                ? payload.frequency_of_sessions.includes("Other")
                : payload.frequency_of_sessions === "Other";

            if (!hasOtherFrequency) {
                otherFrequencyOfSession = undefined;
            } else {
                otherFrequencyOfSession = payload.other_frequency_of_sessions;
            }
        }

        if ("location_of_the_client" in payload) {
            otherLocationOfTheClient =
                payload.location_of_the_client === ClientLocations.Other
                    ? payload.other_location_of_the_client
                    : "";
        }

        if ("location_of_the_provider" in payload) {
            otherLocationOfTheProvider =
                payload.location_of_the_provider === ProviderLocations.Other
                    ? payload.other_location_of_the_provider
                    : "";
        }

        if ("when_should_follow_up_be_done_for_this_client" in payload) {
            const value = payload.when_should_follow_up_be_done_for_this_client;
            if (value) {
                const isDateFormat = !Number.isNaN(new Date(value).getTime());
                whenShouldFollowUpBeDoneForThisClient = isDateFormat
                    ? `${handleFormatDatePickerValue(value)}T00:00:00.000`
                    : value;
            } else {
                whenShouldFollowUpBeDoneForThisClient = null;
            }
        }

        if ("last_session_date" in payload) {
            lastSessionDate = payload.last_session_date
                ? formatZonedTimeToUtc(payload.last_session_date)
                : null;
        }

        if ("first_session_date" in payload) {
            firstSessionDate = payload?.first_session_date
                ? formatZonedTimeToUtc(payload.first_session_date)
                : null;
        }

        if ("date_of_next_session" in payload) {
            dateOfNextSession = payload?.date_of_next_session
                ? formatZonedTimeToUtc(payload?.date_of_next_session)
                : null;
        }

        if ("date_of_first_session" in payload) {
            dateOfFirstSession = payload?.date_of_first_session
                ? formatZonedTimeToUtc(payload.date_of_first_session)
                : null;
        }

        if ("date_of_last_session" in payload) {
            dateOfLastSession = payload?.date_of_last_session
                ? formatZonedTimeToUtc(payload.date_of_last_session)
                : null;
        }

        if ("date_of_last_exam" in payload) {
            dateOfLastExam = payload?.date_of_last_exam
                ? formatZonedTimeToUtc(payload?.date_of_last_exam)
                : null;
        }

        if ("date_of_review" in payload) {
            dateOfReview = payload?.date_of_review
                ? formatZonedTimeToUtc(payload?.date_of_review)
                : null;
        }

        if ("record_of_disclosure_of_phi_ephi" in payload) {
            recordOfDisclosureOfPhiEphiDate =
                payload?.record_of_disclosure_of_phi_ephi
                    ? formatZonedTimeToUtc(
                          payload?.record_of_disclosure_of_phi_ephi
                      )
                    : null;
        }

        if ("date" in payload) {
            evaluationDate = payload?.date
                ? formatZonedTimeToUtc(payload?.date)
                : null;
        }
        if ("date_of_update" in payload) {
            dateOfUpdate = payload?.date_of_update
                ? formatZonedTimeToUtc(payload.date_of_update)
                : null;
        }

        if ("date_created" in payload) {
            dateCreated = payload?.date_created
                ? formatZonedTimeToUtc(payload.date_created)
                : null;
        }

        if ("expected_termination_date" in payload) {
            expectedTerminationDate = payload?.expected_termination_date
                ? formatZonedTimeToUtc(payload?.expected_termination_date)
                : null;
        }

        if (
            "patients_ability_and_capability_to_respond_to_treatment" in payload
        ) {
            const shouldHaveDetails =
                payload.patients_ability_and_capability_to_respond_to_treatment
                    ? payload.patients_ability_and_capability_to_respond_to_treatment.includes(
                          "Diminished/poor or limited-quality patient-oriented evidence"
                      )
                    : false;

            detailsForDiminishedPoorLimitedQualityPatientOrientedEvidence =
                shouldHaveDetails
                    ? payload.details_for_diminished_poor_limited_quality_patient_oriented_evidence
                    : "";
        }

        if ("anxiety" in payload) {
            if (payload.anxiety) {
                anxietyDetails = payload.anxiety_details;
                anxietyFrequency = payload.anxiety_frequency;
            } else {
                anxietyDetails = "";
                anxietyFrequency = "";
            }
        }

        if ("panic_attacks" in payload) {
            if (payload.panic_attacks) {
                panicAttacksDetails = payload.panic_attacks_details;
                panicAttacksFrequency =
                    "panic_attacks_frequency" in payload
                        ? payload.panic_attacks_frequency
                        : payload.panic_attacks_freqency;
            } else {
                panicAttacksDetails = "";
                panicAttacksFrequency = "";
            }
        }

        if ("depression" in payload) {
            if (payload.depression) {
                depressionDetails = payload.depression_details;
                depressionFrequency =
                    "depression_frequency" in payload
                        ? payload.depression_frequency
                        : payload.depression_freqency;
            } else {
                depressionDetails = "";
                depressionFrequency = "";
            }
        }

        if ("family" in payload) {
            const arrayOfShouldHaveDetails = payload.family.map((fam) => {
                return (
                    fam.patients_ability_and_capability_to_respond_to_treatment?.includes(
                        "Diminished/poor or limited-quality patient-oriented evidence"
                    ) || false
                );
            });

            arrayOfdetailsForDiminishedPoorLimitedQualityPatientOrientedEvidences =
                payload.family.map((fam, i) =>
                    arrayOfShouldHaveDetails[i]
                        ? fam.details_for_diminished_poor_limited_quality_patient_oriented_evidence
                        : ""
                );

            family = payload.family.map((fam, i: number) => ({
                ...fam,
                anxiety_details: fam.anxiety ? fam.anxiety_details : "",
                anxiety_frequency: fam.anxiety ? fam.anxiety_frequency : "",
                panic_attacks_details: fam.panic_attacks
                    ? fam.panic_attacks_details
                    : "",
                panic_attacks_freqency: fam.panic_attacks
                    ? fam.panic_attacks_freqency
                    : "",
                depression_details: fam.depression
                    ? fam.depression_details
                    : "",
                depression_freqency: fam.depression
                    ? fam.depression_freqency
                    : "",
                details_for_diminished_poor_limited_quality_patient_oriented_evidence:
                    arrayOfdetailsForDiminishedPoorLimitedQualityPatientOrientedEvidences[
                        i
                    ],
                describe_been_so_distressed_you_seriously_wished_to_end_your_life:
                    getConditionalDescribeValue(
                        fam,
                        "been_so_distressed_you_seriously_wished_to_end_your_life",
                        "describe_been_so_distressed_you_seriously_wished_to_end_your_life"
                    ),
                describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
                    getConditionalDescribeValue(
                        fam,
                        "has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live",
                        "describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live"
                    ),
                describe_a_specific_plan_of_how_you_would_kill_yourself:
                    getConditionalDescribeValue(
                        fam,
                        "a_specific_plan_of_how_you_would_kill_yourself",
                        "describe_a_specific_plan_of_how_you_would_kill_yourself"
                    ),
                describe_access_to_weapons_or_means_of_hurting_self:
                    getConditionalDescribeValue(
                        fam,
                        "access_to_weapons_or_means_of_hurting_self",
                        "describe_access_to_weapons_or_means_of_hurting_self"
                    ),
                describe_made_serious_suicide_attempts:
                    getConditionalDescribeValue(
                        fam,
                        "made_serious_suicide_attempts",
                        "describe_made_serious_suicide_attempts"
                    ),
                describe_done_something_to_hurt_yourself:
                    getConditionalDescribeValue(
                        fam,
                        "purposely_done_something_to_hurt_yourself",
                        "describe_done_something_to_hurt_yourself"
                    ),
                describe_heard_voices_telling_you_to_hurt_yourself:
                    getConditionalDescribeValue(
                        fam,
                        "heard_voices_telling_you_to_hurt_yourself",
                        "describe_heard_voices_telling_you_to_hurt_yourself"
                    ),
                describe_had_relatives_who_attempted_or_commited_sucide:
                    getConditionalDescribeValue(
                        fam,
                        "had_relatives_who_attempted_or_commited_sucide",
                        "describe_had_relatives_who_attempted_or_commited_sucide"
                    ),
                describe_had_relatives_who_attempted_or_committed_suicide:
                    getConditionalDescribeValue(
                        fam,
                        "had_relatives_who_attempted_or_committed_suicide",
                        "describe_had_relatives_who_attempted_or_committed_suicide"
                    ),
                describe_had_thoughts_of_killing_or_seriously_hurting_someone:
                    getConditionalDescribeValue(
                        fam,
                        "had_thoughts_of_killing_or_seriously_hurting_someone",
                        "describe_had_thoughts_of_killing_or_seriously_hurting_someone"
                    ),
                describe_heard_voices_telling_you_to_hurt_others:
                    getConditionalDescribeValue(
                        fam,
                        "heard_voices_telling_you_to_hurt_others",
                        "describe_heard_voices_telling_you_to_hurt_others"
                    ),
                describe_hurt_someone_or_destroyed_property_on_purpose:
                    getConditionalDescribeValue(
                        fam,
                        "hurt_someone_or_destroyed_property_on_purpose",
                        "describe_hurt_someone_or_destroyed_property_on_purpose"
                    ),
                describe_slapped_kicked_punched_someone_with_intent_to_harm:
                    getConditionalDescribeValue(
                        fam,
                        "slapped_kicked_punched_someone_with_intent_to_harm",
                        "describe_slapped_kicked_punched_someone_with_intent_to_harm"
                    ),
                describe_been_arrested_or_detained_for_violent_behavior:
                    getConditionalDescribeValue(
                        fam,
                        "been_arrested_or_detained_for_violent_behavior",
                        "describe_been_arrested_or_detained_for_violent_behavior"
                    ),
                describe_been_to_jail_for_any_reason:
                    getConditionalDescribeValue(
                        fam,
                        "been_to_jail_for_any_reason",
                        "describe_been_to_jail_for_any_reason"
                    ),
                describe_been_on_probation_for_any_reason:
                    getConditionalDescribeValue(
                        fam,
                        "been_on_probation_for_any_reason",
                        "describe_been_on_probation_for_any_reason"
                    ),
                describe_do_you_have_access_to_guns:
                    getConditionalDescribeValue(
                        fam,
                        "do_you_have_access_to_guns",
                        "describe_do_you_have_access_to_guns"
                    ),
                family_id: fam?.family_id || uuidv4(),
            }));
        }

        return {
            noteId,
            data: {
                pos_code: posCode,
                note_content: {
                    ...payload,
                    diagnosis_codes: diagnosisCodes || undefined,
                    delivery_option: deliveryOption,
                    other_delivery_option: otherDeliveryOption,
                    provider_location: providerLocation,
                    other_provider_location: otherProviderLocation,
                    consent_was_received_for_the_teletherapy_session:
                        consentWasReceivedForTheTeletherapySession,
                    the_teletherapy_session_was_conducted_via_video:
                        theTeletherapySessionWasConductedViaVideo,
                    when_should_follow_up_be_done_for_this_client:
                        whenShouldFollowUpBeDoneForThisClient,
                    first_session_date: firstSessionDate,
                    last_session_date: lastSessionDate,
                    date_of_next_session: dateOfNextSession,
                    date_of_first_session:
                        "date_of_first_session" in payload
                            ? dateOfFirstSession
                            : undefined,
                    date_of_last_session:
                        "date_of_last_session" in payload
                            ? dateOfLastSession
                            : undefined,
                    expected_termination_date: expectedTerminationDate,
                    date_of_last_exam: dateOfLastExam,
                    date_of_review: dateOfReview,
                    record_of_disclosure_of_phi_ephi:
                        recordOfDisclosureOfPhiEphiDate,
                    date: evaluationDate,
                    date_of_update: dateOfUpdate,
                    date_created: dateCreated,
                    other_frequency_of_sessions: otherFrequencyOfSession,
                    other_location_of_the_client:
                        "location_of_the_client" in payload
                            ? otherLocationOfTheClient
                            : undefined,
                    other_location_of_the_provider:
                        "location_of_the_provider" in payload
                            ? otherLocationOfTheProvider
                            : undefined,
                    details_for_diminished_poor_limited_quality_patient_oriented_evidence:
                        detailsForDiminishedPoorLimitedQualityPatientOrientedEvidence,
                    family,
                    secondary_diagnosis: secondaryDiagnosisCode || undefined,
                    tertiary_diagnosis: tertiaryDiagnosisCode || undefined,
                    additional_diagnosis_one:
                        additionalDiagnosisOne || undefined,
                    additional_diagnosis_two:
                        additionalDiagnosisTwo || undefined,
                    anxiety_details: anxietyDetails,
                    anxiety_frequency: anxietyFrequency,
                    panic_attacks_details: panicAttacksDetails,
                    panic_attacks_freqency:
                        "panic_attacks_freqency" in payload
                            ? panicAttacksFrequency
                            : undefined,
                    panic_attacks_frequency:
                        "panic_attacks_frequency" in payload
                            ? panicAttacksFrequency
                            : undefined,
                    depression_details: depressionDetails,
                    depression_frequency:
                        "depression_frequency" in payload
                            ? depressionFrequency
                            : undefined,
                    depression_freqency:
                        "depression_freqency" in payload
                            ? depressionFrequency
                            : undefined,
                    describe_been_so_distressed_you_seriously_wished_to_end_your_life:
                        getConditionalDescribeValue(
                            payload,
                            "been_so_distressed_you_seriously_wished_to_end_your_life",
                            "describe_been_so_distressed_you_seriously_wished_to_end_your_life"
                        ),
                    describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live:
                        getConditionalDescribeValue(
                            payload,
                            "has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live",
                            "describe_has_anything_happened_recently_to_make_you_feel_you_dont_want_to_live"
                        ),
                    describe_a_specific_plan_of_how_you_would_kill_yourself:
                        getConditionalDescribeValue(
                            payload,
                            "a_specific_plan_of_how_you_would_kill_yourself",
                            "describe_a_specific_plan_of_how_you_would_kill_yourself"
                        ),
                    describe_access_to_weapons_or_means_of_hurting_self:
                        getConditionalDescribeValue(
                            payload,
                            "access_to_weapons_or_means_of_hurting_self",
                            "describe_access_to_weapons_or_means_of_hurting_self"
                        ),
                    describe_made_serious_suicide_attempts:
                        getConditionalDescribeValue(
                            payload,
                            "made_serious_suicide_attempts",
                            "describe_made_serious_suicide_attempts"
                        ),
                    describe_done_something_to_hurt_yourself:
                        getConditionalDescribeValue(
                            payload,
                            "purposely_done_something_to_hurt_yourself",
                            "describe_done_something_to_hurt_yourself"
                        ),
                    describe_heard_voices_telling_you_to_hurt_yourself:
                        getConditionalDescribeValue(
                            payload,
                            "heard_voices_telling_you_to_hurt_yourself",
                            "describe_heard_voices_telling_you_to_hurt_yourself"
                        ),
                    describe_had_relatives_who_attempted_or_commited_sucide:
                        getConditionalDescribeValue(
                            payload,
                            "had_relatives_who_attempted_or_commited_sucide",
                            "describe_had_relatives_who_attempted_or_commited_sucide"
                        ),
                    describe_had_relatives_who_attempted_or_committed_suicide:
                        getConditionalDescribeValue(
                            payload,
                            "had_relatives_who_attempted_or_committed_suicide",
                            "describe_had_relatives_who_attempted_or_committed_suicide"
                        ),
                    describe_had_thoughts_of_killing_or_seriously_hurting_someone:
                        getConditionalDescribeValue(
                            payload,
                            "had_thoughts_of_killing_or_seriously_hurting_someone",
                            "describe_had_thoughts_of_killing_or_seriously_hurting_someone"
                        ),
                    describe_heard_voices_telling_you_to_hurt_others:
                        getConditionalDescribeValue(
                            payload,
                            "heard_voices_telling_you_to_hurt_others",
                            "describe_heard_voices_telling_you_to_hurt_others"
                        ),
                    describe_hurt_someone_or_destroyed_property_on_purpose:
                        getConditionalDescribeValue(
                            payload,
                            "hurt_someone_or_destroyed_property_on_purpose",
                            "describe_hurt_someone_or_destroyed_property_on_purpose"
                        ),
                    describe_slapped_kicked_punched_someone_with_intent_to_harm:
                        getConditionalDescribeValue(
                            payload,
                            "slapped_kicked_punched_someone_with_intent_to_harm",
                            "describe_slapped_kicked_punched_someone_with_intent_to_harm"
                        ),
                    describe_been_arrested_or_detained_for_violent_behavior:
                        getConditionalDescribeValue(
                            payload,
                            "been_arrested_or_detained_for_violent_behavior",
                            "describe_been_arrested_or_detained_for_violent_behavior"
                        ),
                    describe_been_to_jail_for_any_reason:
                        getConditionalDescribeValue(
                            payload,
                            "been_to_jail_for_any_reason",
                            "describe_been_to_jail_for_any_reason"
                        ),
                    describe_been_on_probation_for_any_reason:
                        getConditionalDescribeValue(
                            payload,
                            "been_on_probation_for_any_reason",
                            "describe_been_on_probation_for_any_reason"
                        ),
                    describe_do_you_have_access_to_guns:
                        getConditionalDescribeValue(
                            payload,
                            "do_you_have_access_to_guns",
                            "describe_do_you_have_access_to_guns"
                        ),
                    describe_ever_run_away_from_home:
                        getConditionalDescribeValue(
                            payload,
                            "ever_run_away_from_home",
                            "describe_ever_run_away_from_home"
                        ),
                    describe_ever_been_bullied_or_bullied_someone:
                        getConditionalDescribeValue(
                            payload,
                            "ever_been_bullied_or_bullied_someone",
                            "describe_ever_been_bullied_or_bullied_someone"
                        ),
                },
                date_of_service: data?.data.date_of_service as string,
                session_start_time: sessionStartTime
                    ? formatZonedTimeToUtc(sessionStartTime)
                    : (data?.data.session_start_time as string),
                session_end_time: sessionEndTime
                    ? formatZonedTimeToUtc(sessionEndTime)
                    : (data?.data.session_end_time as string),
                reason_for_short_duration: data?.data.reason_for_short_duration
                    ? data?.data.reason_for_short_duration
                    : undefined,
                expected_note_last_modified_at: noteVersionRef.current,
            },
        };
    };

    const notifyEditConflict = () => {
        hasEditConflictRef.current = true;
        setHasEditConflict(true);
    };

    const getSaveErrorMessage = (error: unknown, fallback: string) => {
        if (isAxiosError<{ message?: string }>(error)) {
            return error.response?.data?.message ?? fallback;
        }

        return fallback;
    };

    const saveDraftNote = async (
        payload: ReturnType<typeof validateInputs>
    ) => {
        const response = await editDraftNote.mutateAsync(payload);

        noteVersionRef.current = response.data.note_last_modified_at;

        return response;
    };

    const isSaveBlocked = () =>
        hasEditConflictRef.current ||
        isNoteSignedRef.current ||
        hasLeftNoteRef.current;

    const enqueueSave = <T>(blockedResult: T, save: () => Promise<T>) => {
        if (isSaveBlocked()) return Promise.resolve(blockedResult);

        return runExclusive(async () => {
            if (isSaveBlocked()) return blockedResult;

            return save();
        });
    };

    const handleSaveError = (error: unknown, fallback: string) => {
        if (isAxiosError(error) && error.response?.status === 409) {
            notifyEditConflict();
            return;
        }

        toast({
            mode: "error",
            message: getSaveErrorMessage(error, fallback),
        });
    };

    const getAutosaveErrorMessage = (error: unknown) => {
        if (isAxiosError(error) && !error.response) {
            return "check your internet connection";
        }

        return getSaveErrorMessage(error, "your latest changes are not saved");
    };

    const handleEditDraftNote = (payload: EditNoteFormsPayload) =>
        enqueueSave<void>(undefined, async () => {
            const payloadToSend = validateInputs(payload);

            try {
                const response = await saveDraftNote(payloadToSend);

                await queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });

                toast({
                    mode: "success",
                    message:
                        response.message || "Draft note updated successfully",
                });

                hasLeftNoteRef.current = true;

                navigate(`/notes/${clientId}/${noteId}?viewFrom=session_note`, {
                    replace: true,
                });
            } catch (error) {
                handleSaveError(
                    error,
                    "Could not edit draft note at this time"
                );
            }
        });

    const handleAutoSaveNote = (payload: EditNoteFormsPayload) =>
        enqueueSave(false, async () => {
            try {
                await saveDraftNote(validateInputs(payload));

                queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });

                setAutosaveStatus({ savedAt: showUserLocalTime() });
                return true;
            } catch (error) {
                if (isAxiosError(error) && error.response?.status === 409) {
                    notifyEditConflict();
                    return false;
                }

                setAutosaveStatus({
                    error: `Autosave failed: ${getAutosaveErrorMessage(error)}`,
                });
                return false;
            }
        });

    const handleSignNote = (payload: EditNoteFormsPayload) =>
        enqueueSave<void>(undefined, async () => {
            const payloadToSend = validateInputs(payload);

            try {
                await saveDraftNote(payloadToSend);

                const response = await signSessionNote.mutateAsync({
                    noteId,
                    data: {
                        signature: payload.signature as string,
                        signature_date_time: new Date(Date.now()).toISOString(),
                    },
                });

                isNoteSignedRef.current = true;

                toast({
                    mode: "success",
                    message: response.message || "Note signed successfully",
                });

                await queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });

                await queryClient.invalidateQueries({
                    queryKey: [
                        practice?.is_supervisor ? `notes` : clientId,
                        `session-note`,
                        noteId,
                    ],
                });

                navigate(`/notes/${clientId}/${noteId}?viewFrom=session_note`, {
                    replace: true,
                });
            } catch (error) {
                handleSaveError(error, "Could not sign note at this time");
            }
        });

    const handleAddNoteDocument = (
        selectedFiles: File[] | null,
        onFormSubmit: () => void
    ) => {
        const formData = new FormData();

        if (selectedFiles && selectedFiles.length > 0) {
            for (let i = 0; i < [...selectedFiles].length; i += 1) {
                formData.append("documents", [...selectedFiles][i]);
            }
            addNoteContentDoc.mutate(
                {
                    noteId,
                    formData,
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                            setUploadProgress(
                                Math.min(
                                    95,
                                    Math.round(
                                        (progressEvent.loaded * 100) /
                                            progressEvent.total
                                    )
                                )
                            );
                        }
                    },
                },
                {
                    onSuccess: async (res) => {
                        queryClient.invalidateQueries({
                            queryKey: [clientId, `session-note`, noteId],
                        });
                        setUploadProgress(0);
                        onFormSubmit();
                        toast({
                            mode: "success",
                            message:
                                res?.message ??
                                "Note document added successfully",
                        });
                    },
                    onError: async (err) => {
                        setUploadProgress(0);
                        toast({
                            mode: "error",
                            message:
                                err?.response?.data?.message ??
                                "Failed to add note document",
                        });

                        Sentry.logger.error("Failed to add note document", {
                            note_id: noteId,
                            error: err?.response?.data?.message || "N/A",
                            errorTrace: err,
                        });
                    },
                }
            );
        }
    };

    const handleDeleteNoteContentDocument = async (documentId: string) => {
        deleteNoteContentDoc.mutate(
            {
                noteId,
                dataToSend: {
                    document_ids: [documentId],
                },
            },
            {
                onSuccess: (res) => {
                    queryClient.invalidateQueries({
                        queryKey: [clientId, `session-note`, noteId],
                    });
                    toast({
                        mode: "success",
                        message:
                            res.message || "Note document deleted successfully",
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err?.response?.data?.message ??
                            "Failed to delete note document",
                    });

                    Sentry.logger.error("Failed to delete note document", {
                        note_id: noteId,
                        error: err?.response?.data?.message || "N/A",
                        errorTrace: err,
                    });
                },
            }
        );
    };

    return {
        handleEditDraftNote,
        handleAutoSaveNote,
        handleSignNote,
        handleAddNoteDocument,
        handleDeleteNoteContentDocument,
        uploadProgress,
    };
}
